<template>
  <div class="flex items-center gap-2">
    <DropdownMenu>
      <DropdownMenuTrigger as-child>
        <Button variant="outline" size="sm" type="button" :class="priorityButtonClass" :loading="isUpdating">
          <span class="truncate"><span v-if="showLabel" class="text-sm text-muted-foreground">
            Priority:
          </span>{{ priorityLabel }}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" class="w-56">
        <DropdownMenuLabel>Change Priority:</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem :disabled="isUpdating" @select="setPriority('low')">
          Low
        </DropdownMenuItem>
        <DropdownMenuItem :disabled="isUpdating" @select="setPriority('standard')">
          Standard
        </DropdownMenuItem>
        <DropdownMenuItem :disabled="isUpdating" @select="setPriority('high')">
          High
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { toast } from 'vue-sonner'
import { Button } from '../../../../../../../../resources/js/components/ui/button/index.js'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '../../../../../../../../resources/js/components/ui/dropdown-menu/index.js'
import { useTasksStore } from '../../../stores/tasksStore.js'

const PRIORITY_LABELS = {
  low: 'Low',
  standard: 'Standard',
  high: 'High',
}

const props = defineProps({
  task: {
    type: Object,
    required: true,
  },
  value: {
    type: [String, Object],
    default: null,
  },
  context: {
    type: String,
    default: 'default',
  },
  showLabel: {
    type: Boolean,
    default: false,
  },
})

const tasksStore = useTasksStore(props.context)
const taskId = computed(() => Number(props.task?.id))
const isUpdating = computed(() => tasksStore.isUpdatingPriority(taskId.value))
const normalizedPriority = computed(() => {
  if (typeof props.value === 'string') {
    return props.value
  }

  if (props.value && typeof props.value === 'object' && 'value' in props.value) {
    return String(props.value.value)
  }

  return null
})
const priorityLabel = computed(() => PRIORITY_LABELS[normalizedPriority.value] ?? 'No priority')
const priorityButtonClass = computed(() => {
  const baseClass = 'h-7 rounded-full border-transparent px-3 text-xs font-semibold shadow-none ring-1 hover:bg-transparent'

  if (normalizedPriority.value === 'high') {
    return `${baseClass} bg-rose-50 text-rose-700 ring-rose-200 dark:bg-rose-950/50 dark:text-rose-200 dark:ring-rose-800`
  }

  if (normalizedPriority.value === 'low') {
    return `${baseClass} bg-slate-100 text-slate-600 ring-slate-200 dark:bg-slate-900/70 dark:text-slate-300 dark:ring-slate-700`
  }

  return `${baseClass} bg-muted text-muted-foreground ring-border`
})

const setPriority = async (priority) => {
  if (!Number.isInteger(taskId.value) || taskId.value <= 0) {
    return
  }

  if (normalizedPriority.value === priority) {
    return
  }

  try {
    await tasksStore.updatePriority(taskId.value, priority)
    toast.success(`Priority set to ${PRIORITY_LABELS[priority]}.`)
  } catch (error) {
    const errorMessage = error?.response?.data?.message
      || error?.response?.data?.errors?.priority?.[0]
      || 'Unable to update the priority.'
    toast.error(errorMessage)
  }
}
</script>
